import { useChannelListQuery } from '@intigral/domain/lib/presentation/hooks/useChannelListQuery';
import { deleteCookie, getCookie } from '@intigral/utils';
import * as React from 'react';
import { Suspense, memo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

// Third-party imports
import { useWindowVirtualizer } from '@tanstack/react-virtual';

// Domain imports
import { Carousel } from '@intigral/domain/lib/core/entities/ScreenEntities';
import { useNewScreenQuery } from '@intigral/domain/lib/presentation/hooks/useNewScreenQuery';
import { RewardCoin, Spinner, toast } from '@intigral/shared';

import { COMPONENT_TYPES, STORE_NAME, ZONE_TYPE } from '@libs/constants';

// Local imports
import ComponentErrorBoundary from '../components/ComponentErrorBoundary';
import h, { getCountry, getProfileRating } from '../helpers/common';
import user from '../helpers/user';
import useChannelList from '../hooks/useChannelList';
import usePPVConfig from '../hooks/usePPVConfig';
import { IStore } from '../interfaces/StoreTypes';
import { ComponentMapper } from '../managers/componentMapper';
import { RowFallback } from '../managers/rowFallback';
import { handleDataLoaderType } from '../managers/dataLoaderHandler';
import { filterComponents } from '../utils/filterComponents';
import { getUserInfo } from '../utils/user';
import { RenderedComponent } from './ScreenCarousel';

import Loader from '../components/Loader';
import { getZoneApi } from '../services/serviceWrapper/thinkAnalyticsWrapper';
import { useConfigurationSections } from '../hooks/useConfiguration';
import { isGuestUser } from '../config/guestMode';
import { getActiveCarouselsFromLayout } from '../utils/getActiveCarouselsFromLayout';
import { GracePeriodHomePopup } from './GracePeriodHomePopup';

/**
 * Props for the NewScreen component
 */
type NewScreenProps = {
    screenKey: string;
    screenName: string;
    screenId?: null | string;
};

type NewScreenComponentProps = {
    screenKey: string;
    screenId?: null | string;
    components: any[];
    isLoading: boolean;
    isError: boolean;
};

// Memoized RenderedComponent for performance optimization
const MemoizedRenderedComponent = memo(RenderedComponent, (prevProps, nextProps) => {
    return prevProps.carousel === nextProps.carousel;
});

/**
 * Helper function to measure element size for virtualization
 */
const measureElement = (() => {
    let timeoutId: NodeJS.Timeout;

    return (element: HTMLDivElement | null, windowVirtualizer: any) => {
        if (!(element && element instanceof Element)) return;
        const instance = windowVirtualizer;
        const direction = instance.scrollDirection;

        if (direction === 'forward' || direction === null) {
            windowVirtualizer.measureElement(element);
        } else {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                windowVirtualizer.measureElement(element);
            }, 100);
        }
    };
})();

/**
 * Virtualized Row component for optimized rendering
 */
import { truncateDomain } from '@intigral/utils';
import { getSession, removeSession, setSession } from '@intigral/utils/lib/browser-storage/localStorage';
import { VirtualItem } from '@tanstack/react-virtual';
import { fetchUserRevision } from '../store/reducers/userRevisionSlice';
import useGamification from '../features/gamification/hooks/useGamification';
import { isGamificationEnabled } from '../features/gamification/utils/GamificationUtils';

interface VirtualizedRowProps {
    virtualItem: VirtualItem;
    components: any[];
    dataLoader: (carousel: any) => Promise<any>;
    renderComponent: (carousel: any, data: any, carouselIndex: number) => React.ReactElement;
    windowVirtualizer: any;
}

const VirtualizedRow = memo(
    ({ virtualItem, components, dataLoader, renderComponent, windowVirtualizer }: VirtualizedRowProps) => {
        return (
            <div ref={(el) => measureElement(el, windowVirtualizer)} data-index={virtualItem.index}>
                <MemoizedRenderedComponent
                    carousel={components[virtualItem.index]}
                    dataLoader={dataLoader}
                    render={renderComponent}
                    carouselIndex={virtualItem.index}
                />
            </div>
        );
    },
    (prev, next) => {
        return (
            prev.virtualItem.key === next.virtualItem.key &&
            prev.components[prev.virtualItem.index] === next.components[next.virtualItem.index] &&
            prev.dataLoader === next.dataLoader &&
            prev.renderComponent === next.renderComponent
        );
    }
);

/**
 * Component that handles the rendering of screen content with virtualization
 */
const NewScreenComponent = ({
    isLoading,
    isError,
    components,
    screenId = null,
    screenKey,
}: NewScreenComponentProps) => {
    const params = useParams();
    const parentRef = React.useRef<HTMLDivElement>(null);
    const parentOffsetRef = React.useRef(0);
    // Using React Query instead of Redux for configuration
    const { config } = useConfigurationSections();
    const { virtualizer = {} } = (config as any) || {};
    // Moved the early return to after hooks
    const isRtl = params['lang'] !== 'en';
    const sessionStorageName = `${STORE_NAME.SCROLL_OBJECT}`;
    const sessionStorage = getSession(sessionStorageName);
    const virtualScrollSeesion =
        sessionStorage && sessionStorage.screenKey?.toLowerCase() === screenKey?.toLowerCase() ? sessionStorage : null;

    React.useEffect(() => {
        if (
            sessionStorage?.screenKey &&
            screenKey &&
            screenKey?.toLowerCase() !== sessionStorage?.screenKey?.toLowerCase()
        ) {
            removeSession(sessionStorageName);
        }

        const handleBeforeUnload = () => {
            removeSession(sessionStorageName);
        };

        window.addEventListener('pagehide', handleBeforeUnload);

        return () => {
            window.removeEventListener('pagehide', handleBeforeUnload);
        };
    }, [screenKey]);

    React.useLayoutEffect(() => {
        parentOffsetRef.current = virtualScrollSeesion?.parentRefOffsetTop ?? parentRef.current?.offsetTop ?? 0;
    }, []);

    // Configure virtualizer
    const itemSizeMap = new Map();

    const windowVirtualizer = useWindowVirtualizer({
        count: components.length,
        estimateSize: (index) => itemSizeMap.get(index) ?? (virtualizer?.estimateSize || 1280),
        scrollMargin: parentOffsetRef.current,
        overscan: virtualizer?.overscan || 6,
        paddingEnd: virtualizer?.paddingEnd || 0,
        isRtl,
        debug: false,
        initialOffset: () => {
            if (virtualScrollSeesion?.scrollOffset && virtualScrollSeesion?.measurementsCache?.length > 0) {
                return virtualScrollSeesion.scrollOffset;
            }
            return 0;
        },
        initialMeasurementsCache: virtualScrollSeesion?.measurementsCache ?? [],
        /**
         * IMPORTANT: `react-virtual` doesn't include margins in its calculations. If margins are used to space out
         * components, the measurements will be off and cause visual issues (shifting layout) during scrolling.
         * Instead of using margins, use padding inside the component (component container) to ensure accurate
         * measurements.
         * Another potential option is to set container `display` to `flex` and use `gap` for spacing
         *
         * This is a known limitation of `react-virtual` - https://github.com/TanStack/virtual/discussions/734
         */
        measureElement: (el) => {
            if (el instanceof HTMLElement) {
                const index = el.dataset.index ? Number(el.dataset.index) : undefined;
                if (index !== undefined) {
                    const height = el.getBoundingClientRect().height;
                    itemSizeMap.set(index, height);
                    return height;
                }
            }
            return virtualizer?.estimateSize || 1280; // fallback estimate
        },
    });

    // Setup data dependencies
    const selectedProfile = useSelector((store: IStore) => store.profile);
    const geolocation = useSelector((store: IStore) => store.location);

    // Data loading handler
    const dataLoader = useCallback(
        async (carousel) => {
            return await handleDataLoaderType(carousel as Carousel, null, selectedProfile, geolocation);
        },
        [selectedProfile, geolocation]
    );

    // Component rendering handler
    const renderComponent = useCallback((carousel, data, carouselIndex = null, isLoading = false) => {
        return (
            <ComponentErrorBoundary>
                <Suspense fallback={<RowFallback carousel={carousel} />}>
                    <ComponentMapper
                        carousel={carousel}
                        data={data}
                        screenId={screenId}
                        carouselIndex={carouselIndex}
                        isLoading={isLoading}
                    />
                </Suspense>
            </ComponentErrorBoundary>
        );
    }, []);

    const items = windowVirtualizer.getVirtualItems();
    const scrollSessionRef = React.useRef({
        scrollOffset: windowVirtualizer?.scrollOffset ?? 0,
        measurementsCache: windowVirtualizer?.measurementsCache ?? [],
        parentRefOffsetTop: parentRef.current?.offsetTop || 0,
        screenKey,
    });

    // Keep latest scroll metrics in memory; persist once on unmount to avoid
    // flooding sessionStorage (and third-party listeners like Kameleoon) on every scroll.
    React.useEffect(() => {
        scrollSessionRef.current = {
            scrollOffset: windowVirtualizer?.scrollOffset ?? 0,
            measurementsCache: windowVirtualizer?.measurementsCache ?? [],
            parentRefOffsetTop: parentRef.current?.offsetTop || 0,
            screenKey,
        };
    }, [windowVirtualizer?.measurementsCache, windowVirtualizer?.scrollOffset, screenKey]);

    React.useEffect(() => {
        return () => {
            const snapshot = scrollSessionRef.current;
            if (snapshot?.scrollOffset && snapshot?.measurementsCache?.length) {
                setSession(sessionStorageName, snapshot);
            }
        };
    }, [sessionStorageName]);

    if (isLoading || components === null) return <Spinner opaqueBg />;
    if (!components?.length) return null;

    return (
        <div ref={parentRef} style={{ alignSelf: 'flex-start' }}>
            <div
                style={{
                    height: `${windowVirtualizer.getTotalSize()}px`,
                    width: '100%',
                    position: 'relative',
                    overflowAnchor: 'none',
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${items[0]?.start - windowVirtualizer.options.scrollMargin}px)`,
                        // top: `translateY(${items[0]?.start - windowVirtualizer.options.scrollMargin}px)`,

                        // Possible alternative styles to avoid layout shifts
                        // display: 'flex',
                        // flexDirection: 'column',
                        // gap: 'var(--step)',
                        // gap: '0.8rem',
                    }}
                >
                    {items.map((virtualItem) => (
                        <VirtualizedRow
                            key={virtualItem.key}
                            virtualItem={virtualItem}
                            components={components}
                            dataLoader={dataLoader}
                            renderComponent={renderComponent}
                            windowVirtualizer={windowVirtualizer}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

const ZONE_ORDER_TIMEOUT_MS = 5000;

// getZoneCarouselOrder is a function that gets the carousel order from the zone data source
async function getZoneCarouselOrder(zoneDataSource, zone, currentComponents) {
    if (!zone) {
        return currentComponents;
    }

    // If no data source, return current components unchanged (don't revert to original)
    if (!zoneDataSource) {
        return currentComponents;
    }

    try {
        // fetch data from dataSources
        const zoneApiRes = await Promise.race([
            getZoneApi(zoneDataSource),
            new Promise<string[]>((resolve) => setTimeout(() => resolve([]), ZONE_ORDER_TIMEOUT_MS)),
        ]);

        if (!zoneApiRes || !Array.isArray(zoneApiRes) || zoneApiRes.length === 0) {
            return currentComponents;
        }

        // Use current components as the working set
        const workingComponents = [...currentComponents];

        const zoneLinkedCarousels = zone.linkedCarousels || [];
        const zonePositionInComponents = workingComponents.findIndex((component) => component.id === zone.id);

        // If zone not found in current components, return unchanged
        if (zonePositionInComponents === -1) {
            return currentComponents;
        }

        // useCasesOrdering is the array of useCaseIds that are used to order the components
        const useCasesOrdering = zoneApiRes;

        // used as the array to store the components that are in the zone and have ordering data
        const tempComponents = [];

        // Find components that belong to this zone and have ordering data
        workingComponents.forEach((component) => {
            if (
                component.useCaseId &&
                useCasesOrdering.includes(component.useCaseId) &&
                zoneLinkedCarousels.includes(component.id)
            ) {
                tempComponents.push(component);
            }
        });

        // Only proceed if we have components to reorder
        if (tempComponents.length === 0) {
            return currentComponents;
        }

        // reorder tempComponents based on useCasesOrdering
        const orderedComponents = tempComponents.sort((a, b) => {
            const indexA = useCasesOrdering.indexOf(a.useCaseId);
            const indexB = useCasesOrdering.indexOf(b.useCaseId);
            return indexA - indexB;
        });

        // remove all components in zone.linkedCarousels from workingComponents
        const filteredComponentsWithoutLinkedCarousels = workingComponents.filter(
            (component) => !zoneLinkedCarousels.includes(component.id)
        );

        // Find the updated position of the zone after filtering
        const updatedZonePosition = filteredComponentsWithoutLinkedCarousels.findIndex(
            (component) => component.id === zone.id
        );

        // add orderedComponents right after the zone
        if (updatedZonePosition !== -1) {
            filteredComponentsWithoutLinkedCarousels.splice(updatedZonePosition + 1, 0, ...orderedComponents);
        }

        return filteredComponentsWithoutLinkedCarousels;
    } catch (error) {
        return currentComponents;
    }
}

// handleZoneComponents is a function that handles the zone components
async function handleZoneComponents(
    filteredComponents,
    zoneComponentsLoaded,
    setZoneComponentsLoaded,
    setZoneComponents
) {
    const zones = filteredComponents.filter((component) => component.type === ZONE_TYPE && component.active);

    if (!zoneComponentsLoaded) {
        try {
            // Start with filtered components as the base
            let currentComponents = [...filteredComponents];

            // Process zones sequentially to avoid race conditions
            for (let i = 0; i < zones.length; i++) {
                const zone = zones[i];
                const zoneDataSource = zone?.dataSources?.[0];

                // Always pass the current state to maintain accumulation
                // Whether zone has data source or not, we preserve current ordering
                const updatedComponents = await getZoneCarouselOrder(zoneDataSource, zone, currentComponents);

                // Update current components with the result
                currentComponents = updatedComponents;
            }

            // Set the final processed components
            setZoneComponents(currentComponents);
            setZoneComponentsLoaded(true);
        } catch (error) {
            // Fallback to original filtered components on major error
            setZoneComponents(filteredComponents);
            setZoneComponentsLoaded(true);
        }
    }
}

/**
 * Main screen component that handles data fetching and initial setup
 */
const NewScreen: React.FC<NewScreenProps> = ({ screenKey, screenName, screenId = null }) => {
    const [zoneComponents, setZoneComponents] = useState([]);
    const [zoneComponentsLoaded, setZoneComponentsLoaded] = useState(false);
    const gamificationConfig = isGamificationEnabled();

    usePPVConfig();
    const userInfo = getUserInfo();
    //  FIXED: Get configuration for user tier GUID
    const { config } = useConfigurationSections();
    const { isLoading, isError, data, error } = useNewScreenQuery(
        screenName,
        screenKey,
        'deviceType=pc',
        userInfo?.apiKey
    );
    //  Configuration already available from above destructuring

    const { updateChannelData } = useChannelList();
    const { getNotifications } = useGamification();

    const dispatch = useDispatch();
    const navigate = useNavigate();

    React.useEffect(() => {
        if (gamificationConfig?.enable) {
            getNotifications();
        }
        dispatch(fetchUserRevision());
        const isOnColdStartPage = location.pathname.includes('cold-start');
        const isOnConsentManagementPage = location.pathname.includes('consent-management');
        const isOnParentalPinManagementPage = location.pathname.includes('parental-pin-management');
        if (!isOnColdStartPage && !isOnConsentManagementPage && !isOnParentalPinManagementPage) {
            const redirectUrl = sessionStorage.getItem('redirect') || getCookie('redirect') || null;
            if (redirectUrl !== null) {
                const truncatedUrl = truncateDomain(redirectUrl);
                if (truncatedUrl) {
                    if (truncatedUrl.includes('account')) {
                        window.location.href = truncatedUrl;
                    } else {
                        navigate(truncatedUrl);
                    }
                }
                deleteCookie('redirect');
                sessionStorage.removeItem('redirect');
            }
        }
    }, []);

    React.useEffect(() => {
        setZoneComponentsLoaded(false);
        setZoneComponents([]);
        lastfilteredComponents.current = null;
    }, [screenName, screenKey]);

    const { data: channelData } = useChannelListQuery(
        {
            apiKey: user.getAPIKey(),
            country: getCountry(),
            deviceType: h.getDeviceType(),
            tierGuid: user.getUserActiveTierGuidV2(config),
            filter: '', // This will get all channels
            device: h.getDevice(),
            ...(user.getProfileMode() === 'kid' && { categoryType: 'kids' }),
        },
        getProfileRating()
    );

    // Move updateChannelData to useEffect to avoid state updates during render
    React.useEffect(() => {
        if (channelData) {
            updateChannelData(channelData);
        }
    }, [channelData, updateChannelData]);

    const isGuest = isGuestUser(getUserInfo());
    const components = React.useMemo(() => getActiveCarouselsFromLayout(data, isGuest), [data, isGuest]);

    // memoiezed filterComponents
    const filteredComponents = React.useMemo(() => filterComponents(components), [components]);

    // memoized zones
    const zones = React.useMemo(
        () => filteredComponents.filter((component) => component.type === ZONE_TYPE && component.active),
        [filteredComponents]
    );

    const lastfilteredComponents = React.useRef(null);

    React.useEffect(() => {
        if (zones.length === 0) {
            return;
        }

        if (JSON.stringify(filteredComponents) === JSON.stringify(lastfilteredComponents.current)) {
            return;
        }

        lastfilteredComponents.current = filteredComponents;
        handleZoneComponents(filteredComponents, false, setZoneComponentsLoaded, setZoneComponents);
    }, [filteredComponents, screenKey, zones.length]);

    if (zones.length === 0) {
        return (
            <>
                <NewScreenComponent
                    components={filteredComponents}
                    isError={isError}
                    isLoading={isLoading}
                    screenId={screenId}
                    screenKey={screenKey}
                />
                <GracePeriodHomePopup />
            </>
        );
    }

    if (isLoading || !zoneComponentsLoaded) {
        return (
            <>
                <Spinner opaqueBg />
            </>
        );
    }

    return (
        <>
            <NewScreenComponent
                components={zoneComponents}
                isError={isError}
                isLoading={isLoading || !zoneComponentsLoaded}
                screenId={screenId}
                screenKey={screenKey}
            />
            <GracePeriodHomePopup />
        </>
    );
};

export { NewScreenComponent, NewScreen as default };
